// Greenhold <BalanceDisplay> component. Stories 1.15 + 1.17, two pots in 8.2,
// cash-out removed in 8.3.
//
// Two variants share the same balance fetch + cache:
//
//   * `compact` (default) — top-nav chip: balance amount + Top-up button.
//     Story 1.15 shipped this. Shows the WALLET pot only: the chip is the
//     "what can I spend right now" affordance.
//   * `full` — account-page card: the wallet pot, the creator earnings pot
//     when there is one, provenance breakdown bar, a Top-up action, and the
//     transaction-history link.
//
// ---------------------------------------------------------------------------
// STORY 8.2 — TWO POTS. THE ONE RULE THIS FILE MUST NOT BREAK.
// ---------------------------------------------------------------------------
//
// A user has TWO balances and they are NEVER added together, here or anywhere:
//
//   * the WALLET pot (`balance_cents`) — purchased, closed-loop, spendable on
//     Greenhold and NOT payable out;
//   * the EARNINGS pot (`earnings_cents`) — creator revenue share on content
//     supplied. Payable out (Story 8.5) and convertible ONE WAY into the
//     wallet pot, free.
//
// One headline figure would say "you have €X" and imply all of it is
// redeemable, which is exactly the claim the no-payout pivot denies. Two
// figures with two labels is the product decision AND the compliance posture.
//
// COPY DISCIPLINE (UX-DR49, word list authored in
// _bmad-output/implementation-artifacts/8-2-two-pots-wallet-and-creator-earnings.md):
// the wallet pot is never "your money", "funds", "cash" or "balance you can
// withdraw", and neither pot is ever described in appreciation or
// investment-return terms. "Earnings", "revenue share" and "paid out" are
// correct for the EARNED pot and wrong for the wallet pot. The two
// vocabularies must not cross.
//
// ---------------------------------------------------------------------------
// STORY 8.3 — THERE IS NO CASH-OUT CONTROL HERE, AND THERE IS NO PLACEHOLDER
// FOR ONE. THIS IS THE MOST IMPORTANT LINE IN THE FILE.
// ---------------------------------------------------------------------------
//
// The wallet pot is NOT payable. What was removed, and why each piece had to
// go rather than be disabled:
//
//   * the "Cash out" / "Link bank to cash out" button and its aria-labels;
//   * the `€25 minimum cashout` hint (FR7's threshold);
//   * `onCashoutClick`, `onLinkBankClick`, `paymentAccountStatus`,
//     `PaymentAccountStatusSummary`, `BankAccountSummary`.
//
// ⚠ THE PROPS HAD TO GO WITH THE BUTTON. Every disabled control in this file
// falls through to `title="Coming soon"`. Deleting `features/payout/` and
// leaving the button here would have rendered a greyed-out Cash out button
// labelled "Coming soon" — a promise that the feature is NOT YET, when
// the truth is NEVER. That is a dormant knob in the most literal sense, and
// Story 8.1 recorded it as contradiction #5: the real cash-out affordance was
// never in `features/payout/` at all, it was HERE, in the shared package,
// with zero test coverage.
//
// The earnings pot keeps its own action ("Move to balance") because that move
// is INTERNAL and free. Creator PAYOUT is Story 8.5 and belongs on a creator
// surface, not on the wallet card — putting it back here would re-merge the
// two vocabularies the pot split exists to keep apart.

import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { cn } from "../lib/utils";

const BALANCE_QUERY_KEY = ["wallet-balance"] as const;
const BALANCE_STALE_MS = 30_000;
const BALANCE_CACHE_MS = 5 * 60_000;
const LOW_BALANCE_CENTS = 500;

export type BalanceSnapshot = {
  /** The WALLET pot: purchased, spendable on Greenhold, not payable out. */
  balance_cents: number;
  escrow_cents: number;
  /**
   * The EARNINGS pot (Story 8.2): creator revenue share. Payable, and
   * convertible one way into the wallet pot. Optional so an older cached
   * response still renders; absent is treated as zero, never as "unknown".
   */
  earnings_cents?: number;
  currency: "EUR";
};

export type ProvenanceBreakdownSegment = {
  /** Display label, e.g. "From top-ups", "From marketplace sales". */
  label: string;
  /** 0–1 share of the total balance attributable to this provenance. */
  ratio: number;
  /** Tailwind background-color class. */
  colorClass: string;
};

export type BalanceDisplayProps = {
  /**
   * `compact` (default) — top-nav variant: balance amount + Top-up button.
   * `full` — account-page variant per UX-DR19. Ships in Story 1.17.
   */
  variant?: "compact" | "full";
  /** Top-up CTA handler. Compact + full. */
  onTopupClick?: () => void;
  /**
   * Compact variant only. When provided, the balance amount renders as a
   * button that calls this handler (the top-bar pill navigates to the
   * wallet page). When omitted, the amount is a plain, non-interactive span.
   */
  onBalanceClick?: () => void;
  /**
   * Full-variant only. Pre-computed provenance breakdown segments
   * (ratios sum to 1.0; consumer aggregates from `/wallet/history`).
   * When omitted, the bar is hidden.
   */
  provenanceBreakdown?: ProvenanceBreakdownSegment[];
  /**
   * Full-variant only. Story 8.2 — handler for "Move to balance": the
   * one-way, free conversion of earned revenue share into spendable wallet
   * balance. When omitted the action is disabled. There is deliberately no
   * inverse handler; wallet → earnings does not exist.
   */
  onConvertEarningsClick?: () => void;
  /**
   * Full-variant only. Href the "View transaction history" link points
   * at. Defaults to `/account/wallet/history`.
   */
  historyHref?: string;
  /** Sentry correlation-id forwarder. */
  onResponse?: (response: Response) => void;
  /** Additional Tailwind classes appended to the outer wrapper. */
  className?: string;
};

/** EUR-format cents as a display string: 5250 → "€52.50". */
export function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const absCents = Math.abs(cents);
  const euros = Math.floor(absCents / 100);
  const remainder = absCents % 100;
  return `${sign}€${euros}.${remainder.toString().padStart(2, "0")}`;
}

async function fetchBalance(
  onResponse: ((response: Response) => void) | undefined,
): Promise<BalanceSnapshot> {
  const response = await fetch("/api/v1/wallet/balance", {
    credentials: "include",
  });
  onResponse?.(response);
  if (!response.ok) {
    throw new Error(`balance fetch failed: ${response.status}`);
  }
  return (await response.json()) as BalanceSnapshot;
}

export function BalanceDisplay({
  variant = "compact",
  onTopupClick,
  onBalanceClick,
  provenanceBreakdown,
  onConvertEarningsClick,
  historyHref = "/account/wallet/history",
  onResponse,
  className,
}: BalanceDisplayProps) {
  const query = useQuery({
    queryKey: BALANCE_QUERY_KEY,
    queryFn: () => fetchBalance(onResponse),
    staleTime: BALANCE_STALE_MS,
    gcTime: BALANCE_CACHE_MS,
    retry: false,
  });

  const onRetry = useCallback(() => {
    void query.refetch();
  }, [query]);

  if (variant === "compact") {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-2 font-heading",
          className,
        )}
        data-testid="balance-display"
      >
        {query.isPending ? (
          <Skeleton className="h-5 w-16" aria-label="Loading balance" />
        ) : query.isError || !query.data ? (
          <>
            <span
              className="text-sm text-muted-foreground"
              aria-label="Balance unavailable"
            >
              —
            </span>
            <button
              type="button"
              onClick={onRetry}
              className="text-xs underline decoration-dotted"
              aria-label="Retry balance fetch"
            >
              retry
            </button>
          </>
        ) : onBalanceClick ? (
          <button
            type="button"
            onClick={onBalanceClick}
            className={cn(
              "rounded text-base font-semibold tabular-nums transition-colors hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              query.data.balance_cents < LOW_BALANCE_CENTS && "text-amber-700",
            )}
            aria-label={`Current balance: ${formatEur(
              query.data.balance_cents,
            )} EUR. View wallet`}
          >
            {formatEur(query.data.balance_cents)}
          </button>
        ) : (
          <span
            className={cn(
              "text-base font-semibold tabular-nums",
              query.data.balance_cents < LOW_BALANCE_CENTS && "text-amber-700",
            )}
            aria-label={`Current balance: ${formatEur(
              query.data.balance_cents,
            )} EUR`}
          >
            {formatEur(query.data.balance_cents)}
          </span>
        )}
        {/* Compact top-up affordance: a circular gold-hairline button sized
            to sit intentionally inside the 56px dark shell chrome. A
            centered SVG plus glyph (not a bare text "+"), hover restyles
            color/opacity only per the DS calm-chrome rule. */}
        <button
          type="button"
          onClick={onTopupClick}
          disabled={!onTopupClick}
          title={!onTopupClick ? "Coming soon" : undefined}
          aria-label="Top up balance"
          className={cn(
            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border",
            "border-brand-gold-500/40 text-brand-gold-300 transition-colors",
            "hover:border-brand-gold-500/70 hover:text-brand-gold-200",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold-500",
            "focus-visible:ring-offset-1 focus-visible:ring-offset-brand-forest-900",
            "disabled:cursor-not-allowed disabled:opacity-40",
            "disabled:hover:border-brand-gold-500/40 disabled:hover:text-brand-gold-300",
          )}
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden="true"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>
    );
  }

  // ---- variant === "full" — UX-DR19 account-page card ----
  const balanceCents = query.data?.balance_cents ?? 0;
  // Story 8.2 — the second pot. Rendered as its own block with its own label
  // and its own action, NEVER folded into the figure above. A user with no
  // earned revenue share sees nothing extra: an empty "Creator earnings €0.00"
  // card would advertise a payable balance to people who have none.
  const earningsCents = query.data?.earnings_cents ?? 0;
  const hasEarnings = earningsCents > 0;

  return (
    <div
      className={cn(
        "flex flex-col gap-6 rounded-lg border bg-card p-6 font-heading text-card-foreground",
        className,
      )}
      data-testid="balance-display-full"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm font-normal text-muted-foreground font-body">
          Your Greenhold balance
        </p>
        {query.isPending ? (
          <Skeleton className="h-9 w-32" aria-label="Loading balance" />
        ) : query.isError || !query.data ? (
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold tabular-nums text-muted-foreground">
              —
            </span>
            <button
              type="button"
              onClick={onRetry}
              className="text-sm underline decoration-dotted"
              aria-label="Retry balance fetch"
            >
              retry
            </button>
          </div>
        ) : (
          <span
            className={cn(
              "text-3xl font-bold tabular-nums",
              balanceCents < LOW_BALANCE_CENTS && "text-amber-700",
            )}
            aria-label={`Current balance: ${formatEur(balanceCents)} EUR`}
          >
            {formatEur(balanceCents)}
          </span>
        )}
        <p className="text-xs text-muted-foreground font-body">
          Spend it on Greenhold — dossiers and the marketplace.
        </p>
      </div>

      {/* ---- Story 8.2: the EARNED pot. A separate figure, a separate
          label, a separate action. Never summed with the wallet pot above. */}
      {hasEarnings && (
        <div
          className="flex flex-col gap-2 rounded-md border border-brand-gold-500/40 bg-brand-gold-500/5 p-4"
          data-testid="earnings-pot"
        >
          <p className="text-sm font-normal text-muted-foreground font-body">
            Creator earnings
          </p>
          <span
            className="text-2xl font-bold tabular-nums"
            aria-label={`Creator earnings: ${formatEur(earningsCents)} EUR`}
          >
            {formatEur(earningsCents)}
          </span>
          <p className="text-xs text-muted-foreground font-body">
            Your revenue share on dossiers featuring you. Kept separate from
            your balance.
          </p>
          <div>
            <Button
              type="button"
              variant="secondary"
              onClick={onConvertEarningsClick}
              disabled={!onConvertEarningsClick}
              title={!onConvertEarningsClick ? "Coming soon" : undefined}
              aria-label="Move earnings to your Greenhold balance"
            >
              Move to balance
            </Button>
          </div>
          <p className="text-xs text-muted-foreground font-body">
            Moving earnings to your balance is free and cannot be undone.
          </p>
        </div>
      )}

      {provenanceBreakdown && provenanceBreakdown.length > 0 && (
        <div className="space-y-1">
          <div
            className="flex h-2 w-full overflow-hidden rounded"
            role="img"
            aria-label="Balance provenance breakdown"
          >
            {provenanceBreakdown.map((seg) => (
              <div
                key={seg.label}
                className={cn("h-full", seg.colorClass)}
                style={{ width: `${Math.max(0, Math.min(1, seg.ratio)) * 100}%` }}
                title={`${seg.label}: ${(seg.ratio * 100).toFixed(0)}%`}
              />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground font-body">
            {provenanceBreakdown.map((seg) => (
              <li key={seg.label} className="flex items-center gap-1">
                <span className={cn("h-2 w-2 rounded-full", seg.colorClass)} />
                <span>{seg.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Story 8.3 — Top up is the only wallet-pot action. There is no
          second button and no disabled placeholder beside it: see the file
          header on why a greyed-out "Coming soon" would have been worse
          than the live button it replaced. */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="primary"
            onClick={onTopupClick}
            disabled={!onTopupClick}
            title={!onTopupClick ? "Coming soon" : undefined}
            aria-label="Top up balance"
          >
            Top up
          </Button>
        </div>
      </div>

      <a
        href={historyHref}
        className="self-start text-sm font-body text-primary underline decoration-dotted"
        aria-label="View full transaction history"
      >
        View transaction history
      </a>
    </div>
  );
}
